import 'url-search-params-polyfill';
import React, { Suspense, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Route, BrowserRouter as Router, Navigate, useParams, useLocation, Routes, useNavigate,
} from 'react-router-dom';
import { Api } from '@shell/shell-ui';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { toast } from 'react-toastify';

import startsWith from 'lodash/startsWith';
import ProtectedRoute from '@utils/ProtectedRoute';
import NoAuthRoute from '@utils/NoAuthRoute';
import { useActions } from '@hooks/useActions';
import { setUser } from '@redux/modules/user/actions';

import Navbar from '@components/navbar/Navbar';
import Footer from '@components/footer/Footer';
import Loader from '@components/loader/Loader';
import MaintenanceMode from '@components/maintenance-mode/MaintenanceMode';
import ErrorHandler from '@components/error-handler/ErrorHandler';

const LoginPage = React.lazy(() => import('@pages/login-page/LoginPage'));
const RegisterPage = React.lazy(() => import('@pages/register-page/RegisterPage'));
const RegisterConfirm = React.lazy(() => import('@pages/register-confirm/RegisterConfirm'));
const LoginToken = React.lazy(() => import('@pages/login-token/LoginToken'));

const NotFoundPage = React.lazy(() => import('@pages/not-found/NotFoundPage'));
const HomePage = React.lazy(() => import('@pages/home-page/HomePage'));
const Documents = React.lazy(() => import('@pages/documents/Documents'));
const Document = React.lazy(() => import('@pages/document/Document'));
const UserProfile = React.lazy(() => import('@pages/user-profile/UserProfile'));
const ContactPerson = React.lazy(() => import('@pages/contact-person/ContactPerson'));
const LowCarbonPage = React.lazy(() => import('@pages/low-carbon/LowCarbonPage'));
const News = React.lazy(() => import('@pages/news/News'));
const NewsDetails = React.lazy(() => import('@pages/news/NewsDetails'));
const Search = React.lazy(() => import('@pages/search/Search'));
const MyDocs = React.lazy(() => import('@pages/my-docs/MyDocs'));
const DocumentGroupsPage = React.lazy(() => import('@pages/document-groups/DocumentGroups'));
const TermsAndConditions = React.lazy(() => import('@pages/terms-and-conditions/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('@pages/privacy-policy/PrivacyPolicy'));
const IUSPage = React.lazy(() => import('@pages/ius/IUSPage'));
const ShopPage = React.lazy(() => import('@pages/ius/shop/ShopPage'));
const IROPage = React.lazy(() => import('@pages/iro/IRO'));
const LayoutBrowser = React.lazy(() => import('@pages/layout-browser/LayoutBrowser'));
const DeviationProcess = React.lazy(() => import('@pages/deviation-process/DeviationProcess'));
const ShopRedirectPage = React.lazy(() => import('@pages/shop/ShopRedirectPage'));
const ToolkitsPage = React.lazy(() => import('@pages/toolkits/Toolkits'));
const EmailResetPage = React.lazy(() => import('@pages/email-reset/EmailReset'));
const ProjectsPage = React.lazy(() => import('@pages/projects/Projects'));
const SitesPage = React.lazy(() => import('@pages/sites/Sites'));

export function ScrollToTop() {
  const { pathname } = useLocation();
  const { trackPageView } = useMatomo();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView();
  }, [pathname]);

  return null;
}

function RedirectTo({ path, withId }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const idParam = withId ? `/${id}` : '';
  return navigate(`/${path}${idParam}`);
}

RedirectTo.defaultProps = {
  withId: false,
};

RedirectTo.propTypes = {
  path: PropTypes.string.isRequired,
  withId: PropTypes.bool,
};

function isNoGetUserMePath() {
  return ['/register', '/register-confirm'].some((path) => startsWith(window.location.pathname, path));
}

async function handleErrorFromQuery() {
  const queryParams = new URLSearchParams(window.location.search);
  if (queryParams.has('message') && queryParams.has('status')) {
    let toastMessage = '';
    const queryStatus = queryParams.get('status');
    const queryMessage = queryParams.get('message');
    const isUserNotFoundQueryError = queryMessage === 'User not found' && queryStatus === '404';
    const isProblemWithGeneratingTOkenQueryError = queryMessage === 'Problem with generating access token' && queryStatus === '500';
    if (isUserNotFoundQueryError) {
      toastMessage = 'Your user is currently blocked. Please contact Shell sponsor or Site administrator.';
    }
    if (isProblemWithGeneratingTOkenQueryError) {
      toastMessage = 'There was a problem please refresh the page.';
    }
    if (isUserNotFoundQueryError || isProblemWithGeneratingTOkenQueryError) {
      toast.error(toastMessage, {
        autoClose: 5000,
      });
      await new Promise((r) => setTimeout(r, 3000));
      window.history.replaceState(null, '', window.location.pathname);
    }
  }
}

function ProductRedirect() {
  const { id } = useParams();
  return <Navigate to={`/document-groups/${id}`} replace />;
}

function App() {
  const [loading, setLoading] = useState(true);
  const [setUserAction] = useActions([setUser]);
  const { pushInstruction } = useMatomo();

  useEffect(() => {
    async function loadUser() {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        void handleErrorFromQuery();
        if (queryParams.has('refetchRefreshToken')) {
          await Api.post('/oauth/refresh', null, {}, {
            baseURL: GIDS_API,
          });
          window.history.replaceState(null, '', window.location.pathname);
        }
        if (!isNoGetUserMePath()) {
          const { data } = await Api.get('/user/me');
          setUserAction(data);
          sessionStorage.setItem('logged', 'true');
          pushInstruction('setUserId', data?.id);
        }
      } catch (e) {
        console.log({ e });
      }
    }

    loadUser().then(
      () => setLoading(false),
    );
  }, []);

  return (
    loading ? (
      <Loader fullScreen main />
    ) : (
      <Router>
        <Suspense
          fallback={<Loader fullScreen main />}
        >
          <ScrollToTop />
          <Navbar />
          <ErrorHandler>
            <MaintenanceMode>
              <Routes>
                <Route path='/login' element={<NoAuthRoute component={LoginPage} />} />
                <Route path='/register/:token?' element={<NoAuthRoute component={RegisterPage} />} />
                <Route path='/register-confirm/:token' element={<NoAuthRoute component={RegisterConfirm} />} />
                <Route path='/login-token/:token' element={<NoAuthRoute component={LoginToken} />} />
                <Route
                  exact
                  path='/resetting/request'
                >
                  {() => <Navigate to='/restore-account' />}
                </Route>
                <Route
                  exact
                  path='/registration/request-access'
                >
                  {() => <Navigate to='/register' />}
                </Route>

                <Route
                  exact
                  path='/usage-terms'
                  element={<TermsAndConditions />}
                />
                <Route
                  exact
                  path='/privacy-policy'
                  element={<PrivacyPolicy />}
                />

                <Route
                  path='*'
                  element={(
                    <ProtectedRoute>
                      <div id='main-grid'>
                        <Routes>
                          <Route
                            exact
                            path='/'
                            element={<HomePage />}
                          />

                          <Route
                            exact
                            path='/documents/:id?'
                            element={<Documents />}
                          />

                          <Route
                            exact
                            path='/document/:id'
                            element={<Document />}
                          />

                          <Route
                            exact
                            path='/profile'
                            element={<UserProfile />}
                          />

                          <Route
                            exact
                            path='/contact-person'
                            element={<ContactPerson />}
                          />
                          <Route
                            exact
                            path='/low-carbon'
                            element={<LowCarbonPage />}
                          />

                          <Route
                            exact
                            path='/news'
                            element={<News />}
                          />

                          <Route
                            exact
                            path='/news/iro'
                            element={<IROPage />}
                          />

                          <Route
                            exact
                            path='/news/:id'
                            element={<NewsDetails />}
                          />

                          <Route
                            exact
                            path='/search'
                            element={<Search />}
                          />

                          <Route
                            exact
                            path='/my-docs'
                            element={<MyDocs />}
                          />

                          <Route
                            exact
                            path='/document-groups/:id'
                            element={<DocumentGroupsPage />}
                          />

                          <Route
                            exact
                            path='/toolkits'
                            element={<ToolkitsPage />}
                          />

                          <Route path='/product/:id' exact element={<ProductRedirect />} />

                          <Route path='/low_carbon' exact element={<Navigate to='/low-carbon' />} />

                          <Route
                            exact
                            path='/404'
                            element={<NotFoundPage />}
                          />

                          <Route
                            exact
                            path='/ius'
                            element={<IUSPage />}
                          />

                          <Route
                            exact
                            path='/ius/shop'
                            element={<ShopPage />}
                          />

                          <Route
                            exact
                            path='/layout-browser'
                            element={<LayoutBrowser />}
                          />

                          <Route
                            exact
                            path='/dp'
                            element={<DeviationProcess />}
                          />

                          <Route
                            exact
                            path='/shop-design/configurator/:projectId'
                            element={<ShopRedirectPage />}
                          />

                          <Route
                            exact
                            path='/projects'
                            element={<ProjectsPage />}
                          />

                          <Route
                            exact
                            path='/sites'
                            element={<SitesPage />}
                          />

                          <Route
                            exact
                            path='/email-reset'
                            element={<EmailResetPage />}
                          />

                          <Route path='*'>
                            {() => <Navigate to='/404' />}
                          </Route>
                        </Routes>
                      </div>
                      <Footer />
                    </ProtectedRoute>
                  )}
                />
              </Routes>
            </MaintenanceMode>
          </ErrorHandler>
        </Suspense>
      </Router>
    )
  );
}

export default App;
